/*
 * Navigation / Route Access Configuration
 * ------------------------------------------------------------------
 * Single ordered source of truth for:
 *   - which route a module lives at
 *   - how to decide whether the current user can access it
 *   - the application's navigation priority (used to pick the first
 *     accessible module for redirects)
 *
 * Each entry:
 *   key            string    stable identifier for the module
 *   path           string    static route (mutually exclusive with getPath)
 *   getPath(ctx)   function  resolves a route that depends on runtime data
 *                            (ctx: { role, empId })
 *   permissionCode string?   RBAC feature code (e.g. "dashboard.view"). When
 *                            present, accessibility is permission-based.
 *   roles          string[]  legacy role list. Used as the accessibility
 *                            check ONLY when permissionCode is absent, i.e.
 *                            for modules not yet migrated to RBAC.
 *
 * ORDER MATTERS: this list mirrors the sidebar's navigation priority.
 * getFirstAccessiblePath() walks it top to bottom and returns the first
 * module the user can access. A module migrates to RBAC by adding a
 * permissionCode — nothing else about this file changes.
 */

import { FEATURE_CODES, HIRING_MANAGEMENT_FEATURE_CODES } from "../util/featureCodes";

export const NAVIGATION_ORDER = [
  {
    key: "dashboard",
    path: "/",
    permissionCode: FEATURE_CODES.DASHBOARD_VIEW,
    roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
  },
  {
    key: "employees",
    path: "/allemployees",
    permissionCode: FEATURE_CODES.EMPLOYEE_VIEW,
    roles: ["Super Admin", "Admin", "HR"],
  },
  {
    key: "leave_management",
    path: "/leave_management",
    permissionCode: FEATURE_CODES.LEAVE_VIEW_MODULE,
    roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
  },
  {
    key: "salary",
    path: "/salary",
    permissionCode: FEATURE_CODES.SALARY_VIEW,
    roles: ["Super Admin", "Admin"],
  },
  {
    key: "payslip",
    getPath: ({ empId }) => `/payslip/${empId}`,
    permissionCode: FEATURE_CODES.PAYSLIP_VIEW,
    roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
  },
  {
    key: "resource_tracking",
    path: "/resource_tracking",
    permissionCode: FEATURE_CODES.RESOURCE_VIEW,
    roles: ["Super Admin", "Admin", "HR"],
  },
  {
    key: "reimbursement",
    path: "/reimbursement",
    permissionCode: FEATURE_CODES.REIMBURSEMENT_VIEW,
    roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
  },
  {
    key: "expenditure",
    path: "/expenditure",
    permissionCode: FEATURE_CODES.EXPENDITURE_VIEW,
    roles: ["Super Admin", "Admin", "HR"],
  },
  {
    key: "tenant",
    path: "/tenant",
    roles: ["Super Admin"],
  },
  {
    key: "settings",
    path: "/settings",
    permissionCode: FEATURE_CODES.SETTINGS_VIEW,
    roles: ["Super Admin", "Admin"],
  },
  // Both WorkLogs paths gate on worklog.view rather than a role list: the two
  // entries exist only because different roles land on a different path, not
  // because they need different access. Without permissionCode a custom role
  // matched neither `roles` list and was denied the route even though the
  // sidebar (which is permission-gated) offered it the link.
  {
    key: "worklogs_employee_hr",
    path: "/worklogs",
    permissionCode: FEATURE_CODES.WORKLOG_VIEW,
    roles: ["Employee", "HR"],
  },
  {
    key: "worklogs_lead",
    path: "/worklogs_project",
    permissionCode: FEATURE_CODES.WORKLOG_VIEW,
    roles: ["Lead"],
  },
  {
    key: "email_templates",
    path: "/email_templates",
    permissionCode: FEATURE_CODES.EMAIL_TEMPLATE_VIEW,
    roles: ["Admin", "HR"],
  },
  // HR group
  {
    key: "hiring",
    // HR console for roles with any Hiring management permission; the
    // employee job-openings screen for roles with only default Hiring access.
    getPath: ({ hasPermission }) =>
      HIRING_MANAGEMENT_FEATURE_CODES.some((code) => hasPermission(code))
        ? "/hiring"
        : "/hiring/openings",
    permissionCode: FEATURE_CODES.HIRING_VIEW,
    roles: ["Super Admin", "Admin", "HR"],
  },
  {
    key: "hiring_openings",
    path: "/hiring/openings",
    roles: ["Employee", "Lead"],
  },
  {
    key: "policies",
    path: "/policies",
    permissionCode: FEATURE_CODES.POLICY_VIEW,
    roles: ["Super Admin", "Admin", "HR", "Employee", "Lead"],
  },
  {
    key: "form16",
    path: "/form16",
    permissionCode: FEATURE_CODES.FORM16_VIEW,
    roles: ["Super Admin", "Admin", "HR", "Employee", "Lead"],
  },
  {
    key: "role_permissions",
    path: "/role_permissions",
    roles: ["Super Admin", "Admin"],
  },
  // Billing group
  {
    key: "revenue",
    path: "/revenue",
    permissionCode: FEATURE_CODES.REVENUE_VIEW,
    roles: ["Super Admin", "Admin"],
  },
  {
    key: "invoices",
    path: "/invoices",
    permissionCode: FEATURE_CODES.INVOICE_VIEW,
    roles: ["Super Admin", "Admin"],
  },
  {
    key: "projects",
    path: "/projects",
    permissionCode: FEATURE_CODES.PROJECT_VIEW,
    roles: ["Super Admin", "Admin"],
  },
  {
    key: "clients",
    path: "/clients",
    permissionCode: FEATURE_CODES.CLIENT_VIEW,
    roles: ["Super Admin", "Admin"],
  },
  {
    key: "worklogs_billing",
    getPath: ({ role }) =>
      ["Super Admin", "Admin"].includes(role) ? "/worklogs_project" : "/worklogs",
    permissionCode: FEATURE_CODES.WORKLOG_VIEW,
    roles: ["Super Admin", "Admin", "Employee"],
  },
  // Always-accessible fallback — guarantees getFirstAccessiblePath() terminates
  // for any authenticated, recognized role.
  {
    key: "profile",
    path: "/profile",
    permissionCode: FEATURE_CODES.PROFILE_VIEW,
    roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
  },
];

const resolvePath = (entry, ctx) =>
  entry.getPath ? entry.getPath(ctx) : entry.path;

/**
 * Single source of truth for whether the current user can access a module.
 * Permission-based when the entry has migrated (permissionCode set),
 * legacy role-based otherwise. Used by both ProtectedRoute (route guarding)
 * and NavBarItems (sidebar visibility) so the rule lives in one place.
 *
 * @param {{permissionCode?: string, roles: string[]}} entry
 * @param {{role: string, hasPermission: (code: string) => boolean}} ctx
 * @returns {boolean}
 */
export const isModuleAccessible = (entry, { role, hasPermission }) => {
  if (entry.permissionCode) {
    return hasPermission(entry.permissionCode);
  }
  return Array.isArray(entry.roles) && entry.roles.includes(role);
};

/**
 * Walks NAVIGATION_ORDER and returns the route for the first module the
 * current user can access. Used to replace hardcoded redirect targets
 * (e.g. "/profile", "/") wherever access to the requested module is denied.
 *
 * @param {{role: string, hasPermission: (code: string) => boolean, empId?: string|number}} ctx
 * @returns {string} a route path, or "/login" if nothing matches.
 */
export const getFirstAccessiblePath = (ctx) => {
  for (const entry of NAVIGATION_ORDER) {
    if (isModuleAccessible(entry, ctx)) {
      return resolvePath(entry, ctx);
    }
  }
  return "/login";
};
